import gql from 'graphql-tag'
import { connect } from 'react-redux'
import { compose, withState } from 'recompose'
import {
  createSuccessToasterMessage,
  createErrorToasterMessage,
} from 'data/ui/actions'

import { userGroupAttrsFragment } from 'remote/users'

import { dataFetched, makesQueries, makesMutations } from 'data/graphql'

const getUserGroupsQuery = gql`
  query listUserGroups {
    listUserGroups {
      __typename
      id
      nodes {
        ...userGroupAttrs
      }
    }
  }
  ${userGroupAttrsFragment}
`

export const providesUserGroups = () => {
  return compose(
    withState('fetchUserGroupsState', 'setFetchState', {
      loading: false,
      error: false,
      pristine: true,
    }),
    connect(
      null,
      {
        dataFetched,
      }
    ),
    makesQueries({
      query: getUserGroupsQuery,
      resyncPolicy: 'refetch',
      getOnQueryFromProps: ({ setFetchState }) => () =>
        setFetchState({ loading: true, error: null, pristine: false }),
      getOnResultFromProps: ({ dataFetched, setFetchState }) => ({
        result,
        meta,
      }) => {
        dataFetched(result.data.listUserGroups, meta)
        setFetchState({ loading: false, error: null })
      },
      getOnErrorFromProps({ setError, setFetchState }) {
        return ({ error }) => {
          setFetchState({ loading: false, error })
        }
      },
    })
  )
}

const createUserGroupQuery = gql`
  mutation createUserGroup($input: UserGroupInput!) {
    createUserGroup(input: $input) {
      ...userGroupAttrs
    }
  }
  ${userGroupAttrsFragment}
`

export const providesCreateUserGroup = () =>
  compose(
    connect(
      null,
      {
        dataFetched,
        createSuccessToasterMessage,
        createErrorToasterMessage,
      }
    ),
    makesMutations({
      query: createUserGroupQuery,
      mutationPropName: 'createUserGroup',
      getOnErrorFromProps: ({ createErrorToasterMessage }) => result => {
        createErrorToasterMessage({
          title: 'An error occured',
          detail: result.error.message,
        })
      },
      getOnResultFromProps: ({ dataFetched, createSuccessToasterMessage }) => ({
        result,
        meta,
      }) => {
        createSuccessToasterMessage({
          title: 'Group successfully created',
        })
        dataFetched(result.data.createUserGroup, meta)
      },
    })
  )

const updateUserGroupQuery = gql`
  mutation updateUserGroup($userGroupId: ID!, $input: UserGroupInput!) {
    updateUserGroup(userGroupId: $userGroupId, input: $input) {
      ...userGroupAttrs
    }
  }
  ${userGroupAttrsFragment}
`

export const providesUpdateUserGroup = () =>
  compose(
    connect(
      null,
      {
        dataFetched,
        createSuccessToasterMessage,
        createErrorToasterMessage,
      }
    ),
    makesMutations({
      query: updateUserGroupQuery,
      mutationPropName: 'updateUserGroup',
      getOnErrorFromProps: ({ createErrorToasterMessage }) => result => {
        createErrorToasterMessage({
          title: 'An error occured',
          detail: result.error.message,
        })
      },
      getOnResultFromProps: ({ dataFetched, createSuccessToasterMessage }) => ({
        result,
        meta,
      }) => {
        createSuccessToasterMessage({
          title: 'Group successfully updated',
        })
        dataFetched(result.data.updateUserGroup, meta)
      },
    })
  )

const deleteUserGroupQuery = gql`
  mutation deleteUserGroup($userGroupId: ID!) {
    deleteUserGroup(userGroupId: $userGroupId)
  }
`

export const providesDeleteUserGroup = () =>
  compose(
    connect(
      null,
      {
        dataFetched,
        createSuccessToasterMessage,
        createErrorToasterMessage,
      }
    ),
    makesMutations({
      query: deleteUserGroupQuery,
      mutationPropName: 'deleteUserGroup',
      getOnErrorFromProps: ({ createErrorToasterMessage }) => result => {
        createErrorToasterMessage({
          title: 'An error occured',
          detail: result.error.message,
        })
      },
      getOnResultFromProps: ({ createSuccessToasterMessage }) => () => {
        createSuccessToasterMessage({
          title: 'Group successfully deleted',
        })
      },
    })
  )
